/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import {
    createDemoDeviceSensorCatalog,
    getDeviceDefinitionSystems,
    getDeviceDefinitionVirtualDevices,
    type DeviceDefinitionGroupFilter,
    type SensorDeviceCustomer,
} from '@/lib/sensors/demo/demoDeviceSensors';

/* Import types */
import type { DeviceSensorCsvMetadataGroup } from '@/types/deviceSensor';

export type DeviceDefinitionAvailability = 'enabled' | 'all';
export type DeviceDefinitionCodeType = 'direct' | 'config';
export type DeviceDefinitionSupplyType = Exclude<DeviceDefinitionGroupFilter, 'ALL'>;
export type DeviceDefinitionSupplyTypeMode = 'all' | 'individual';

export interface DeviceDefinitionSupplyTypeOption {
    label: string;
    value: DeviceDefinitionSupplyType;
}

export interface DeviceDefinitionTextGeneratorOptions {
    availability: DeviceDefinitionAvailability;
    codeType?: DeviceDefinitionCodeType;
    customer: SensorDeviceCustomer;
    selectedSupplyTypes: readonly DeviceDefinitionSupplyType[];
    supplyTypeMode: DeviceDefinitionSupplyTypeMode;
    supplyTypeOptions: readonly DeviceDefinitionSupplyTypeOption[];
}

type BuildingSensorField = 'gas' | 'grid_feed' | 'grid_supply' | 'heat' | 'pv' | 'water';

const BUILDING_SENSOR_FIELD_ORDER: readonly BuildingSensorField[] = [
    'grid_feed',
    'grid_supply',
    'pv',
    'heat',
    'gas',
    'water',
];

const BUILDING_SENSOR_FIELDS: Record<DeviceSensorCsvMetadataGroup, BuildingSensorField> = {
    BL: 'grid_supply',
    GAS: 'gas',
    GRID_FEED: 'grid_feed',
    HE: 'heat',
    PV: 'pv',
    WATER: 'water',
};

export class DeviceDefinitionTextGenerator {
    private readonly options: DeviceDefinitionTextGeneratorOptions;

    constructor(options: DeviceDefinitionTextGeneratorOptions) {
        this.options = options;
    }

    generate(): string {
        return this.options.supplyTypeMode === 'all'
            ? this.generateAllSupplyTypes()
            : this.generateSelectedSupplyTypes();
    }

    private get enabledOnly(): boolean {
        return this.options.availability === 'enabled';
    }

    private get codeType(): DeviceDefinitionCodeType {
        return this.options.codeType ?? 'direct';
    }

    private get sortedSupplyTypeOptions(): DeviceDefinitionSupplyTypeOption[] {
        return [...this.options.supplyTypeOptions]
            .sort((left, right) => left.label.localeCompare(right.label));
    }

    private generateAllSupplyTypes(): string {
        const deviceSensorCatalog = createDemoDeviceSensorCatalog(this.options.customer);
        const systems = getDeviceDefinitionSystems(deviceSensorCatalog, this.enabledOnly);

        if (this.codeType === 'config') {
            return this.renderSystemsConfig(systems, deviceSensorCatalog);
        }

        return systems
            .map((system) => this.renderBuildingDefinition(
                system.system,
                new Map(system.groups.map((group) => [group.group, group.devices])),
                deviceSensorCatalog,
            ))
            .join('\n\n');
    }

    private generateSelectedSupplyTypes(): string {
        const deviceSensorCatalog = createDemoDeviceSensorCatalog(this.options.customer);
        const selectedSupplyTypes = new Set(this.options.selectedSupplyTypes);
        const selectedOptions = this.sortedSupplyTypeOptions.filter((option) => (
            selectedSupplyTypes.has(option.value)
        ));

        if (selectedOptions.length === 1) {
            const selectedOption = selectedOptions[0];

            if (!selectedOption) {
                return '';
            }

            return this.renderMeterList(getDeviceDefinitionVirtualDevices(
                deviceSensorCatalog,
                selectedOption.value,
                this.enabledOnly,
            ));
        }

        const deviceGroups = selectedOptions.map((option) => ({
            devices: getDeviceDefinitionVirtualDevices(
                deviceSensorCatalog,
                option.value,
                this.enabledOnly,
            ),
            label: option.label,
        }));

        return this.renderMeterList(deviceGroups);
    }

    private renderBuildingDefinition(
        system: string,
        devicesByGroup: Map<DeviceSensorCsvMetadataGroup, string[]>,
        deviceSensorCatalog: ReturnType<typeof createDemoDeviceSensorCatalog>,
    ): string {
        const firstDevice = deviceSensorCatalog.getDevicesBySystem(system)[0];
        const metadata = firstDevice ? deviceSensorCatalog.getSensorMetadata(firstDevice) : null;
        const sensorValues = this.getBuildingSensorValues(devicesByGroup);

        return [
            '$devices[] = Building::create(',
            `    urn: ${this.renderPhpValue(system)},`,
            `    description: ${this.renderPhpValue(metadata?.name ?? null)},`,
            "    uiName: 'Building',",
            '    initialTime: $initialTime,',
            `    address: ${this.renderPhpValue(metadata?.address ?? null)},`,
            `    grid_feed: ${this.renderBuildingSensorValue(sensorValues.grid_feed, true, this.getBuildingSensorSuffix('grid_feed'))}`,
            `    grid_supply: ${this.renderBuildingSensorValue(sensorValues.grid_supply, true, this.getBuildingSensorSuffix('grid_supply'))}`,
            `    pv: ${this.renderBuildingSensorValue(sensorValues.pv, true, this.getBuildingSensorSuffix('pv'))}`,
            `    heat: ${this.renderBuildingSensorValue(sensorValues.heat, true, this.getBuildingSensorSuffix('heat'))}`,
            `    gas: ${this.renderBuildingSensorValue(sensorValues.gas, true, this.getBuildingSensorSuffix('gas'))}`,
            `    water: ${this.renderBuildingSensorValue(sensorValues.water, false, this.getBuildingSensorSuffix('water'))}`,
            ');',
        ].join('\n');
    }

    private renderSystemsConfig(
        systems: ReturnType<typeof getDeviceDefinitionSystems>,
        deviceSensorCatalog: ReturnType<typeof createDemoDeviceSensorCatalog>,
    ): string {
        return [
            'private const SYSTEMS = [',
            ...systems.map((system) => this.renderSystemConfig(
                system.system,
                new Map(system.groups.map((group) => [group.group, group.devices])),
                deviceSensorCatalog,
            )),
            '];',
        ].join('\n');
    }

    private renderSystemConfig(
        system: string,
        devicesByGroup: Map<DeviceSensorCsvMetadataGroup, string[]>,
        deviceSensorCatalog: ReturnType<typeof createDemoDeviceSensorCatalog>,
    ): string {
        const firstDevice = deviceSensorCatalog.getDevicesBySystem(system)[0];
        const metadata = firstDevice ? deviceSensorCatalog.getSensorMetadata(firstDevice) : null;
        const sensorValues = this.getBuildingSensorValues(devicesByGroup);

        return [
            '    [',
            `        'urn' => ${this.renderPhpValue(system)},`,
            `        'description' => ${this.renderPhpValue(metadata?.name ?? system)},`,
            `        'address' => ${this.renderPhpValue(metadata?.address ?? null)},`,
            "        'sensors' => [",
            ...BUILDING_SENSOR_FIELD_ORDER.flatMap((field) => this.renderConfigSensorLines(
                field,
                sensorValues[field],
                this.getBuildingSensorSuffix(field),
            )),
            '        ],',
            '    ],',
        ].join('\n');
    }

    private getBuildingSensorValues(
        devicesByGroup: Map<DeviceSensorCsvMetadataGroup, string[]>,
    ): Record<BuildingSensorField, string[]> {
        return Object.entries(BUILDING_SENSOR_FIELDS).reduce<Record<BuildingSensorField, string[]>>(
            (values, [group, field]) => {
                values[field] = devicesByGroup.get(group as DeviceSensorCsvMetadataGroup) ?? [];

                return values;
            },
            {
                gas: [],
                grid_feed: [],
                grid_supply: [],
                heat: [],
                pv: [],
                water: [],
            },
        );
    }

    private getBuildingSensorSuffix(field: BuildingSensorField): string {
        if (this.options.customer !== 'muenster') {
            return '';
        }

        return field === 'gas' || field === 'water' ? '.V' : '.W';
    }

    private renderConfigSensorLines(
        field: BuildingSensorField,
        devices: string[],
        suffix: string,
    ): string[] {
        if (devices.length === 0) {
            return [`            '${field}' => null,`];
        }

        return [
            `            '${field}' => [`,
            ...devices.map((device) => `                ${this.renderPhpValue(device, suffix)},`),
            '            ],',
        ];
    }

    private renderMeterList(
        deviceGroups: string[] | Array<{ devices: string[]; label: string }>,
    ): string {
        const isGrouped = deviceGroups.length > 0 && typeof deviceGroups[0] !== 'string';
        const lines = isGrouped
            ? (deviceGroups as Array<{ devices: string[]; label: string }>).flatMap(({ devices, label }, index) => [
                ...(index > 0 ? [''] : []),
                `    // ${label}`,
                ...devices.map((device) => `    ${this.renderTypeScriptString(device)},`),
            ])
            : (deviceGroups as string[]).map((device) => `    ${this.renderTypeScriptString(device)},`);

        return ['const METERS = [', ...lines, '];'].join('\n');
    }

    private renderPhpValue(value: string | null, suffix: string = ""): string {
        if (value === null) {
            return 'null';
        }

        return `'${value
            .replaceAll('\\', '\\\\')
            .replaceAll("'", "\\'")
            .replaceAll('\r\n', '\\n')
            .replaceAll('\n', '\\n') + suffix
        }'`;
    }

    private renderBuildingSensorValue(devices: string[], trailingComma: boolean, suffix: string = ""): string {
        const [primaryDevice, ...additionalDevices] = devices;
        const primaryValue = this.renderPhpValue(primaryDevice ?? null, suffix);

        const valueWithSeparator = trailingComma ? `${primaryValue},` : `${primaryValue}`;

        const additionalDevicesWithSuffix = additionalDevices.map(device => `${device}${suffix}`);

        return additionalDevicesWithSuffix.length > 0
            ? `${valueWithSeparator} // ${additionalDevicesWithSuffix.join(', ')}`
            : valueWithSeparator;
    }

    private renderTypeScriptString(value: string): string {
        return JSON.stringify(value);
    }
}
